<template>
  <div class="teams-fields-view container py-4">
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Daten werden geladen...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <div v-else-if="!team" class="alert alert-gradient-warning" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      Bereich nicht gefunden.
    </div>

    <div v-else>
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>
            <font-awesome-icon icon="list-alt" class="me-2" />
            Mitgliederfelder
          </h2>
        <p class="text-muted mb-0">Verwalte die Felder für {{ team.name }}</p>
        </div>
        <BackButton fallback="teams-settings" :params="{ id: teamId }" />
      </div>
      <nav aria-label="breadcrumb" class="mb-4">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
              Bereiche
            </router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link
              :to="{ name: 'teams-detail', params: { id: teamId } }"
              class="text-decoration-none"
            >
              Quittungsverwaltung
            </router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link
              :to="{ name: 'teams-settings', params: { id: teamId } }"
              class="text-decoration-none"
            >
              Bereich-Einstellungen
            </router-link>
          </li>
          <li class="breadcrumb-item active" aria-current="page">Mitgliederfelder</li>
        </ol>
      </nav>
      <!-- Admin Mitgliederfelder Management Ribbon -->
      <div v-if="isAdminOrCreator" class="mb-4">
        <div
          class="card border-0 bg-gradient-primary text-white ribbon-clickable"
          style="cursor: pointer"
          @click="goToSchemaEdit"
        >
          <div class="card-body py-3">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <font-awesome-icon icon="cog" class="me-2" />
                <span class="fw-semibold text-white">Mitgliederfelder-Verwaltung</span>
              </div>
              <div class="d-flex align-items-center gap-3">
                <div class="text-white">

                  <small class="text-white-50">Felder hinzufügen, bearbeiten oder löschen</small>
                </div>
                <button
                  class="btn btn-warning btn-sm d-flex align-items-center"
                  @click.stop="goToSchemaEdit"
                >
                  <font-awesome-icon icon="edit" class="me-1" />
                  <span>bearbeiten</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Missing Required Fields Warning -->
      <div v-if="hasMissingRequiredFields" class="alert alert-gradient-warning mb-4" role="alert">
        <div class="d-flex align-items-center">
          <font-awesome-icon icon="exclamation-triangle" class="fa-2x me-3" />
          <div>
            <h5 class="alert-heading mb-1">Pflichtfelder nicht ausgefüllt</h5>
            <p class="mb-2">
            Es fehlen noch erforderliche Angaben. Bitte füll alle Pflichtfelder aus.
            </p>
            <button
              type="button"
              class="btn btn-warning btn-sm"
              @click="showRequiredFieldsModalHandler"
            >
              <font-awesome-icon icon="edit" class="me-1" />
              Pflichtfelder ausfüllen
            </button>
          </div>
        </div>
      </div>

      <!-- Team Fields -->
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">
            <font-awesome-icon icon="list-alt" class="me-2" />
            Mitgliederfelder
          </h5>
        </div>
        <div class="card-body">
          <div v-if="!team.fields || team.fields.length === 0" class="text-center py-4">
            <font-awesome-icon icon="inbox" size="3x" class="text-muted mb-3" />
            <h5 class="text-muted">Keine Felder definiert</h5>
            <p class="text-muted">Für diesen Bereich wurden noch keine Felder konfiguriert.</p>
          </div>
          <div v-else class="team-fields">
            <div
              v-for="field in team.fields"
              :key="field.id"
              class="quitly-field-item mb-3 p-3 border rounded"
              @click="handleFieldInputModal(field)"
              role="button"
              style="cursor: pointer; transition: all 0.2s ease"
              @mouseenter="$event.target.style.backgroundColor = '#f8f9fa'"
              @mouseleave="$event.target.style.backgroundColor = ''"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="flex-grow-1">
                  <h6 class="mb-1">
                    {{ field.name }}
                    <span v-if="field.required" class="badge bg-danger ms-2">Pflichtfeld</span>
                  </h6>
                  <p class="text-muted small mb-0">{{ getFieldTypeLabel(field.type) }}</p>
                  <div
                    v-if="field.description"
                    class="text-muted small mb-0 mt-1"
                    v-html="sanitizeHtml(field.description)"
                  ></div>
                </div>
                <div class="field-value text-end">
                  <template v-if="getFieldValue(field)">
                    <span
                      v-if="field.type === 'checkbox'"
                      class="badge"
                      :class="
                        getFieldValue(field) === 'true' || getFieldValue(field) === true
                          ? 'bg-success'
                          : 'bg-secondary'
                      "
                    >
                      {{
                        getFieldValue(field) === 'true' || getFieldValue(field) === true
                          ? 'Ja'
                          : 'Nein'
                      }}
                    </span>
                    <span v-else class="text-primary fw-semibold">{{
                      formatFieldAnswer(field)
                    }}</span>
                  </template>
                  <span v-else class="text-muted">
                    <font-awesome-icon icon="minus" class="me-1" />
                    Nicht ausgefüllt
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTeamsStore } from '@/stores/teams'
import { useAuthStore } from '@/stores/auth'
import { showFieldInputModal, showRequiredFieldsModal } from '@/services/notification'
import { teamService } from '@/api/teams'
import BackButton from '@/components/common/BackButton.vue'
import { sanitizeHtml } from '@/utils/fieldUtils'

const route = useRoute()
const router = useRouter()
const teamsStore = useTeamsStore()
const authStore = useAuthStore()

const { selectedTeam } = storeToRefs(teamsStore)

const loading = ref(true)
const error = ref(null)

const teamId = computed(() => route.params.id)
const team = computed(() => selectedTeam.value)
const currentUser = computed(() => authStore.user)

const isAdminOrCreator = computed(() => {
  if (!team.value || !currentUser.value) return false
  return team.value.role === 'teamAdmin' || team.value.creatorId === currentUser.value.id
})

const hasMissingRequiredFields = computed(() => {
  if (!team.value?.fields || !currentUser.value) return false

  return team.value.fields.some((field) => {
    if (!field.required) return false

    if (field.responses && Array.isArray(field.responses)) {
      const userResponse = field.responses.find(
        (response) => response.userId === currentUser.value.id,
      )
      return !userResponse || !userResponse.data || userResponse.data === ''
    }

    if (Array.isArray(field.response)) {
      return field.response.length === 0 || !field.response[0]?.data
    }

    return true
  })
})

const getFieldTypeLabel = (type) => {
  const labels = {
    text: 'Text',
    email: 'E-Mail',
    number: 'Zahl',
    date: 'Datum',
    textarea: 'Textbereich',
    checkbox: 'Checkbox',
    select: 'Auswahl',
  }
  return labels[type] || type
}

const getFieldValue = (field) => {
  if (field.responses && Array.isArray(field.responses)) {
    const userResponse = field.responses.find(
      (response) => response.userId === currentUser.value.id,
    )
    return userResponse?.data
  }

  if (Array.isArray(field.response)) {
    return field.response[0]?.data
  }

  return null
}

const formatFieldAnswer = (field) => {
  const value = getFieldValue(field)
  if (!value) return ''

  if (field.type === 'checkbox') {
    if (typeof value === 'boolean') {
      return value ? 'Ja' : 'Nein'
    }

    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase().trim()
      if (['true', 'ja', 'yes', '1', 'on', 'checked'].includes(lowerValue)) {
        return 'Ja'
      }
      if (['false', 'nein', 'no', '0', 'off', 'unchecked'].includes(lowerValue)) {
        return 'Nein'
      }
    }

    if (typeof value === 'number') {
      return value !== 0 ? 'Ja' : 'Nein'
    }

    return Boolean(value) ? 'Ja' : 'Nein'
  }

  if (field.type === 'date') {
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const date = new Date(value + 'T00:00:00')
        return date.toLocaleDateString('de-DE')
      } else {
        const date = new Date(value)
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('de-DE')
        } else {
          return value
        }
      }
    } catch (e) {
      return value
    }
  }

  return value
}

const goToSchemaEdit = () => {
  
  router.push({
    name: 'teams-detail',
    params: { id: teamId.value },
    query: { edit: 'true', editSchema: 'true' },
  })
}

const showRequiredFieldsModalHandler = async () => {
  if (!team.value?.fields || !currentUser.value) return

  const missingFields = team.value.fields.filter((field) => {
    if (!field.required) return false

    if (field.responses && Array.isArray(field.responses)) {
      const userResponse = field.responses.find(
        (response) => response.userId === currentUser.value.id,
      )
      return !userResponse || !userResponse.data || userResponse.data === ''
    }

    if (Array.isArray(field.response)) {
      return field.response.length === 0 || !field.response[0]?.data
    }

    return true
  })

  if (missingFields.length === 0) return

  await showRequiredFieldsModal(
    missingFields,
    async (values) => {
      for (let i = 0; i < missingFields.length; i++) {
        const field = missingFields[i]
        const value = values[i]
        await saveFieldValue(field, value)
      }
      await teamsStore.loadTeamById(teamId.value)
    },
    () => {
      
    },
  )
}

const handleFieldInputModal = async (field) => {
  const currentValue = getFieldValue(field)

  const result = await showFieldInputModal({
    title: `Feld bearbeiten: ${field.name}`,
    field: {
      id: field.id,
      name: field.name,
      type: field.type,
      required: field.required,
      options: field.options,
      description: field.description,
      placeholder: field.placeholder,
    },
    currentValue,
    onConfirm: async (value) => {
      await saveFieldValue(field, value)
      await teamsStore.loadTeamById(teamId.value)
    },
    onDelete: async () => {
      await saveFieldValue(field, '')
      await teamsStore.loadTeamById(teamId.value)
    },
    onCancel: () => {},
  })
}

const saveFieldValue = async (field, value) => {
  try {
    await teamService.updateTeamFieldResponse(field.id, value)

    if (field.responses && Array.isArray(field.responses)) {
      const existingResponse = field.responses.find(
        (response) => response.userId === currentUser.value.id,
      )

      if (existingResponse) {
        existingResponse.data = value
      } else {
        field.responses.push({
          userId: currentUser.value.id,
          data: value,
        })
      }
    } else if (Array.isArray(field.response)) {
      if (field.response.length > 0) {
        field.response[0].data = value
      } else {
        field.response.push({ data: value })
      }
    }
    await teamsStore.loadTeamById(teamId.value)
  } catch (error) {
    console.error('Error saving field value:', error)
    throw error
  }
}

onMounted(async () => {
  try {
    loading.value = true
    error.value = null

    if (!team.value) {
      await teamsStore.loadTeamById(teamId.value)
    }
  } catch (err) {
    console.error('Fehler beim Laden der Daten:', err)
    error.value = 'Fehler beim Laden der Daten'
  } finally {
    loading.value = false
  }
})
</script>

<style scoped></style>
